import { useEffect, useRef } from 'react';

/**
 * Custom hook that fixes CSS specificity issues in the editor's style panel.
 *
 * PROBLEM:
 * - Canvas shows white (from `.hero-full-bleed .hero-subtitle` - more specific)
 * - Sidebar shows rgb(71 85 105 / 1) (from `.hero-subtitle` - less specific)
 *
 * ROOT CAUSE:
 * GrapesJS Studio SDK's style manager reads CSS rules by matching the component's classes
 * directly. When you select an element with class `hero-subtitle`, it finds the `.hero-subtitle`
 * rule and shows that value, ignoring more specific rules like `.hero-full-bleed .hero-subtitle`.
 *
 * SOLUTION:
 * Intercept CSS rule retrieval and style reading to use computed styles or find the most
 * specific applicable CSS rule. We intercept multiple entry points:
 * 1. CssComposer.getRules - to modify which rules are returned
 * 2. Component.prototype.getStyle - to return computed styles
 * 3. CSS Rule's getStyle - to return computed values when reading rule styles
 *
 * ⚠️ INTERNAL API DEPENDENCY WARNING:
 * This hook modifies GrapesJS internal prototypes (CssRule.prototype.getStyle,
 * Component.prototype.getStyle, CssComposer.getRules) which is fragile and may break
 * across library updates. This approach is necessary because:
 * - GrapesJS v0.22's official StyleManager API does not provide hooks for overriding
 *   rule resolution or computed style retrieval
 * - The Studio SDK does not expose APIs to customize CSS specificity handling
 *
 * VERSION CONSTRAINTS:
 * - GrapesJS is pinned to version 0.22.12 in package.json (no caret prefix)
 * - This hook should be tested after any GrapesJS dependency updates
 *
 * RECOMMENDATIONS:
 * 1. Monitor GrapesJS releases for official APIs that might eliminate this workaround
 * 2. Add integration tests that validate prototype modifications still work after updates
 * 3. Consider creating an issue/feature request with GrapesJS for official API support
 * 4. Document any GrapesJS version changes that break this hook
 */
const useStyleSpecificityFix = (editor: any) => {
  const timeoutIdRef = useRef<number | null>(null);
  const componentSelectedHandlerRef = useRef<((component: any) => void) | null>(null);
  const isSynchronizingRef = useRef<boolean>(false);

  // Store references to original functions for cleanup
  const originalFunctionsRef = useRef<{
    cssRuleGetStyle?: { original: Function; prototype: any } | null;
    cssComposerGetRules?: { original: Function; instance: any } | null;
    componentGetStyle?: { original: Function; prototype: any } | null;
  }>({});

  useEffect(() => {
    if (!editor) return;

    // Reset refs at the start of effect
    originalFunctionsRef.current = {};

    // Wait for editor to be fully initialized
    const initFix = () => {
      const CssComposer = editor.CssComposer;
      if (!CssComposer) {
        // Retry if CssComposer isn't ready yet
        if (typeof window !== 'undefined' && window.setTimeout) {
          timeoutIdRef.current = window.setTimeout(initFix, 100) as unknown as number;
        }
        return;
      }

      // Clear any pending timeout once CssComposer is ready
      if (timeoutIdRef.current !== null && typeof window !== 'undefined' && window.clearTimeout) {
        window.clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }

      // Helper function to convert computed color to GrapesJS format
      const normalizeColor = (color: string): string => {
        if (!color) return color;
        // Convert rgb(r, g, b) to rgb(r g b / 1)
        const rgbMatch = color.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
        if (rgbMatch) {
          return `rgb(${rgbMatch[1]} ${rgbMatch[2]} ${rgbMatch[3]} / 1)`;
        }
        // Convert rgba(r, g, b, a) to rgb(r g b / a)
        const rgbaMatch = color.match(/^rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)$/);
        if (rgbaMatch) {
          return `rgb(${rgbaMatch[1]} ${rgbaMatch[2]} ${rgbaMatch[3]} / ${rgbaMatch[4]})`;
        }
        return color;
      };

      // Helper function to check if something is a valid DOM element
      const isHTMLElement = (el: any): el is HTMLElement => {
        return (
          el &&
          typeof el === 'object' &&
          typeof el.getAttribute === 'function' &&
          typeof el.ownerDocument !== 'undefined'
        );
      };

      // Helper function to get computed style for a property
      const getComputedStyleValue = (el: HTMLElement | null | undefined, prop: string): string | null => {
        if (!isHTMLElement(el) || typeof window === 'undefined') return null;
        try {
          // Get the window from the element's document (handles iframes)
          const win = el.ownerDocument?.defaultView || window;
          const computed = win.getComputedStyle(el);
          // Try both camelCase and kebab-case property access
          const camelProp = prop.replace(/-([a-z])/g, g => g[1].toUpperCase());
          const value = (computed as any)[camelProp] || computed.getPropertyValue(prop);
          return value || null;
        } catch {
          return null;
        }
      };

      // Helper function to check if element has inline style for a property
      const hasInlineStyle = (el: HTMLElement | null | undefined, prop: string): boolean => {
        if (!isHTMLElement(el)) return false;
        try {
          const inlineStyle = el.getAttribute('style');
          if (!inlineStyle) return false;
          // Check if inline style contains the property
          const styleObj = el.style;
          return styleObj && typeof styleObj.getPropertyValue === 'function' && styleObj.getPropertyValue(prop) !== '';
        } catch {
          return false;
        }
      };

      // Helper function to validate CSS property name
      const isValidCssPropertyName = (name: string): boolean => {
        if (!name || typeof name !== 'string') return false;
        // CSS property names should be valid identifiers
        // Allow alphanumeric, hyphens, underscores, but reject anything that looks suspicious
        if (!/^[a-zA-Z][a-zA-Z0-9_-]*$/.test(name) && !/^[a-zA-Z][a-zA-Z0-9_-]*-[a-zA-Z0-9_-]*$/.test(name)) {
          // Allow camelCase properties like fontSize, marginTop, etc.
          if (!/^[a-z][a-zA-Z0-9]*$/.test(name)) {
            return false;
          }
        }
        // Reject property names with HTML-like content
        if (/<[^>]*>/.test(name) || /&[#\w]+;/.test(name)) return false;
        return name.length > 0 && name.length < 100;
      };

      // Helper function to sanitize CSS value by removing problematic characters
      const sanitizeCssValue = (value: string): string => {
        if (!value) return value;

        // Convert to string and trim
        let sanitized = String(value).trim();
        if (!sanitized) return sanitized;

        // Remove HTML-like tags and entities that could break DOM insertion
        sanitized = sanitized
          .replace(/<[^>]*>/g, '') // Remove HTML tags
          .replace(/&[#\w]+;/g, ''); // Remove HTML entities

        // For CSS content property, preserve valid CSS syntax but sanitize quotes
        // CSS content can be: "text", 'text', attr(data), url(...), etc.
        // We need to preserve the structure but ensure quotes are balanced

        // Remove newlines and tabs (keep spaces)
        sanitized = sanitized.replace(/[\r\n\t]/g, ' ');

        // Collapse multiple spaces (but preserve single spaces)
        sanitized = sanitized.replace(/[ \t]+/g, ' ');

        // Remove any remaining control characters except space
        // eslint-disable-next-line no-control-regex
        sanitized = sanitized.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '');

        // Ensure the value doesn't contain unescaped characters that could break HTML/CSS parsing
        // This is especially important for values that might be used in style attributes

        return sanitized;
      };

      // Helper function to validate CSS value before setting
      const isValidCssValue = (value: any): boolean => {
        if (value == null) return false;
        const str = String(value).trim();
        if (str.length === 0) return false;
        if (str.length > 10000) return false; // Reasonable length limit

        // Check for invalid characters that could break CSS parsing or DOM insertion
        // Avoid control characters (except space, tab, newline which we'll sanitize)
        // eslint-disable-next-line no-control-regex
        if (/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/.test(str)) return false;

        // Check for HTML-like content that could break DOM insertion
        // This is critical - HTML tags in CSS values can cause appendChild errors
        if (/<[^>]*>/.test(str)) return false; // HTML tags
        if (/&[#\w]+;/.test(str)) return false; // HTML entities (might be valid but risky)

        // Check for balanced quotes - but account for escaped quotes in CSS
        // CSS allows escaped quotes like \" and \'
        // Count unescaped quotes only
        let singleQuotes = 0;
        let doubleQuotes = 0;
        let escaped = false;

        for (let i = 0; i < str.length; i++) {
          const char = str[i];
          if (escaped) {
            escaped = false;
            continue;
          }
          if (char === '\\') {
            escaped = true;
            continue;
          }
          if (char === "'") singleQuotes++;
          if (char === '"') doubleQuotes++;
        }

        // Quotes should be balanced (even number) for valid CSS
        if (singleQuotes % 2 !== 0 || doubleQuotes % 2 !== 0) {
          // If quotes are unbalanced, reject to prevent parsing errors
          return false;
        }

        // Check for potentially problematic escape sequences
        // CSS escape sequences are generally safe, but we want to be cautious
        // Allow common CSS escapes and hex escapes
        const hasBackslash = str.includes('\\');
        if (hasBackslash) {
          // Check for escape sequences that might be problematic
          // Allow: \n, \t, \r, \", \', \\, \ followed by hex digits, \ followed by space
          const problematicEscapes = /\\[^0-9a-fA-F\s"'\\nrt]/g;
          if (problematicEscapes.test(str)) {
            // Check if it's a valid CSS escape sequence
            // CSS allows: \ followed by hex digits, or \ followed by a character
            // But we'll be conservative and reject anything that looks suspicious
            const suspiciousPattern = /\\[^0-9a-fA-F\s"'\\nrtu]/;
            if (suspiciousPattern.test(str)) {
              return false;
            }
          }
        }

        return true;
      };

      // Helper function to synchronize CSS rule styles with computed styles
      // Separated from getRules to avoid mutating state during read operations.
      // This function explicitly mutates rules, so it should only be called when
      // style synchronization is intentionally needed (e.g., on component selection).
      const synchronizeRuleStyles = (component: any, rules: any[], matchByElement?: HTMLElement) => {
        if (!component || !rules || rules.length === 0) return;

        const view = component.getView?.();
        const el = matchByElement || view?.el;
        if (!isHTMLElement(el) || typeof window === 'undefined') return;

        // Validate and normalize component classes to ensure they're strings
        // GrapesJS getRules expects classes to be an array of strings
        let componentClasses: string[] = [];
        try {
          const classes = component.getClasses?.() || [];
          componentClasses = Array.isArray(classes) ? classes.filter((cls: any) => typeof cls === 'string') : [];
        } catch (error) {
          // If getClasses fails, use empty array
          componentClasses = [];
        }

        const componentId = component.getId?.() || component.get('id') || '';

        rules.forEach((rule: any) => {
          try {
            // ⚠️ INTERNAL API: Using rule.getSelectors() - may break across GrapesJS updates
            // This internal method is used to match CSS rules to components. Test after updates.
            let selectors: string[] = [];
            try {
              const rawSelectors = rule.getSelectors?.() || [];
              // Ensure selectors is an array of strings (GrapesJS might return unexpected formats)
              selectors = Array.isArray(rawSelectors) ? rawSelectors.filter((sel: any) => typeof sel === 'string') : [];
            } catch (error) {
              // If getSelectors fails, use empty array
              selectors = [];
            }

            let matches = false;

            if (matchByElement) {
              // Use element.matches() for more accurate matching when element is provided
              matches = selectors.some((sel: string) => {
                try {
                  if (el.matches && typeof el.matches === 'function') {
                    try {
                      return el.matches(sel);
                    } catch {
                      return false;
                    }
                  }
                  return false;
                } catch {
                  return false;
                }
              });
            } else {
              // Fallback to component-based matching
              matches = selectors.some((sel: string) => {
                if (componentId && sel.includes(`#${componentId}`)) {
                  return true;
                }
                return componentClasses.some((cls: string) => sel.includes(`.${cls}`) || sel === cls);
              });
            }

            if (matches) {
              const ruleStyle = rule.getStyle?.() || {};
              // Start with existing style and only update what needs changing
              const updatedStyle: any = {};
              let hasChanges = false;

              // First, copy over all existing valid properties
              Object.keys(ruleStyle).forEach((prop: string) => {
                if (isValidCssPropertyName(prop)) {
                  const value = ruleStyle[prop];
                  if (value != null && isValidCssValue(String(value))) {
                    updatedStyle[prop] = sanitizeCssValue(String(value));
                  }
                }
              });

              // Then synchronize properties that don't have inline styles
              Object.keys(ruleStyle).forEach((prop: string) => {
                // Validate property name first
                if (!isValidCssPropertyName(prop)) {
                  return; // Skip invalid property names
                }

                if (!hasInlineStyle(el, prop)) {
                  const computedValue = getComputedStyleValue(el, prop);
                  if (computedValue && isValidCssValue(computedValue)) {
                    let normalized = prop === 'color' ? normalizeColor(computedValue) : computedValue;
                    // Sanitize the normalized value to remove problematic characters
                    normalized = sanitizeCssValue(normalized);
                    // Validate sanitized value before setting
                    if (normalized && isValidCssValue(normalized)) {
                      const sanitizedValue = sanitizeCssValue(normalized);
                      if (updatedStyle[prop] !== sanitizedValue) {
                        updatedStyle[prop] = sanitizedValue;
                        hasChanges = true;
                      }
                    }
                  }
                }
              });

              // Apply the updated style to the rule if there are changes
              if (hasChanges && Object.keys(updatedStyle).length > 0) {
                try {
                  // Use optional chaining to safely call setStyle if available
                  if (rule.setStyle && typeof rule.setStyle === 'function') {
                    rule.setStyle(updatedStyle);
                  } else if (rule.set && typeof rule.set === 'function') {
                    // Fallback to Backbone-style set method if setStyle is not available
                    rule.set('style', updatedStyle);
                  }
                } catch (error) {
                  // Silently handle errors when setting style to avoid breaking the sync process
                  console.warn('Error applying updated style to rule:', error);
                }
              }
            }
          } catch (error) {
            // Silently ignore errors for individual rules to avoid breaking the entire process
            console.warn('Error synchronizing rule styles:', error);
          }
        });
      };

      // Intercept CSS Rule's getStyle method to return computed values
      // ⚠️ INTERNAL API: Modifying CssRule.prototype.getStyle - fragile across GrapesJS updates
      // This is necessary because GrapesJS v0.22 does not provide official hooks for
      // customizing CSS rule style retrieval. Test after any GrapesJS version updates.
      const CssRule = editor.CssComposer.CssRule;
      if (CssRule && CssRule.prototype) {
        const originalRuleGetStyle = CssRule.prototype.getStyle;
        if (originalRuleGetStyle) {
          // Store original function for cleanup
          originalFunctionsRef.current.cssRuleGetStyle = {
            original: originalRuleGetStyle,
            prototype: CssRule.prototype,
          };
          CssRule.prototype.getStyle = function (prop?: string) {
            const style = originalRuleGetStyle.call(this, prop);

            // Try to find the component this rule applies to
            // This is tricky - we need to find components that match this rule's selectors
            let selectors: string[] = [];
            try {
              const rawSelectors = this.getSelectors?.() || [];
              // Ensure selectors is an array of strings (GrapesJS might return unexpected formats)
              selectors = Array.isArray(rawSelectors) ? rawSelectors.filter((sel: any) => typeof sel === 'string') : [];
            } catch (error) {
              // If getSelectors fails, return original style
              return style;
            }

            if (selectors.length === 0) {
              return style;
            }

            // Get the selected component from the editor
            const selected = editor.getSelected?.();
            if (!selected) {
              return style;
            }

            const view = selected.getView?.();
            const el = view?.el;
            if (!isHTMLElement(el)) {
              return style;
            }

            // Check if this rule's selectors match the selected element
            const matchesRule = selectors.some((selector: string) => {
              try {
                // Use matches() method if available (most reliable)
                if (el.matches && typeof el.matches === 'function') {
                  try {
                    return el.matches(selector);
                  } catch {
                    // If selector is invalid, fall back to manual checking
                  }
                }

                // Fallback: manual selector matching
                const doc = el.ownerDocument || document;
                // Try using querySelector to see if element matches
                try {
                  // Check if we can find this element using the selector
                  const matches = doc.querySelectorAll(selector);
                  return Array.from(matches).includes(el);
                } catch {
                  // If selector parsing fails, do simple class/id/tag matching
                  const classes = Array.from(el.classList || []);
                  const id = el.id || '';
                  const tagName = el.tagName.toLowerCase();

                  // Simple check for class, id, or tag selectors
                  if (selector.startsWith('.')) {
                    const className = selector.slice(1).split(/[\s>+~:]/)[0];
                    return classes.includes(className);
                  }
                  if (selector.startsWith('#')) {
                    const idName = selector.slice(1).split(/[\s>+~:]/)[0];
                    return id === idName;
                  }
                  // Check if selector contains this element's tag/class/id
                  return (
                    selector.includes(tagName) ||
                    classes.some(cls => selector.includes(`.${cls}`)) ||
                    (id && selector.includes(`#${id}`))
                  );
                }
              } catch {
                return false;
              }
            });

            if (!matchesRule) {
              return style;
            }

            // If we're getting a specific property, check computed style
            if (prop) {
              const hasInline = hasInlineStyle(el, prop);
              if (!hasInline) {
                const computedValue = getComputedStyleValue(el, prop);
                if (computedValue && isValidCssValue(computedValue)) {
                  let normalized = prop === 'color' ? normalizeColor(computedValue) : computedValue;
                  normalized = sanitizeCssValue(normalized);
                  if (normalized && isValidCssValue(normalized)) {
                    return normalized;
                  }
                }
              }
            } else {
              // If getting all styles, merge computed values for properties that don't have inline styles
              // Also sanitize original style values to prevent invalid tokens
              const mergedStyle: any = {};
              const styleProps = Object.keys(style || {});

              // First, sanitize and copy original style values
              for (const styleProp of styleProps) {
                if (isValidCssPropertyName(styleProp)) {
                  const originalValue = style[styleProp];
                  if (originalValue != null && isValidCssValue(String(originalValue))) {
                    const sanitized = sanitizeCssValue(String(originalValue));
                    if (sanitized && isValidCssValue(sanitized)) {
                      mergedStyle[styleProp] = sanitized;
                    }
                  }
                }
              }

              // Then, update with computed values for properties that don't have inline styles
              for (const styleProp of styleProps) {
                if (!hasInlineStyle(el, styleProp)) {
                  const computedValue = getComputedStyleValue(el, styleProp);
                  if (computedValue && isValidCssValue(computedValue)) {
                    let normalized = styleProp === 'color' ? normalizeColor(computedValue) : computedValue;
                    normalized = sanitizeCssValue(normalized);
                    if (normalized && isValidCssValue(normalized)) {
                      mergedStyle[styleProp] = normalized;
                    }
                  }
                }
              }

              return mergedStyle;
            }

            // Sanitize original style before returning
            if (prop && style != null) {
              const originalValue = String(style);
              if (isValidCssValue(originalValue)) {
                const sanitized = sanitizeCssValue(originalValue);
                if (sanitized && isValidCssValue(sanitized)) {
                  return sanitized;
                }
              }
            }
            return style;
          };
        }
      }

      // Intercept CssComposer.getRules to return rules (pure getter, no mutations)
      // ⚠️ INTERNAL API: Modifying CssComposer.getRules - fragile across GrapesJS updates
      // This is necessary because GrapesJS v0.22 does not provide official hooks for
      // customizing CSS rule resolution. Test after any GrapesJS version updates.
      // Note: Style synchronization is handled separately via synchronizeRuleStyles()
      // to avoid mutating state during read operations.
      const originalGetRules = CssComposer.getRules;
      if (originalGetRules) {
        // Store original function for cleanup
        originalFunctionsRef.current.cssComposerGetRules = {
          original: originalGetRules,
          instance: CssComposer,
        };
        // Bind the original function to CssComposer to preserve context
        const boundOriginalGetRules = originalGetRules.bind(CssComposer);
        CssComposer.getRules = function (component: any) {
          // Pure getter - no mutations. Style synchronization happens explicitly
          // via synchronizeRuleStyles() when needed (e.g., in component:selected handler).
          // Preserve original return type exactly (may be Backbone Collection, array, or other)
          // Use bound function to ensure correct context (CssComposer instance)
          // Pass through the component argument (getRules typically takes one argument)
          return boundOriginalGetRules(component);
        };
      }

      // Intercept Component.prototype.getStyle to return computed styles
      // ⚠️ INTERNAL API: Modifying Component.prototype.getStyle - fragile across GrapesJS updates
      // This is necessary because GrapesJS v0.22 does not provide official hooks for
      // customizing component style retrieval. Test after any GrapesJS version updates.
      const Component = editor.Components.Component;
      if (Component && Component.prototype) {
        const originalGetStyle = Component.prototype.getStyle;

        // Only patch getStyle if originalGetStyle exists
        if (originalGetStyle) {
          // Store original function for cleanup
          originalFunctionsRef.current.componentGetStyle = {
            original: originalGetStyle,
            prototype: Component.prototype,
          };

          Component.prototype.getStyle = function (prop?: string) {
            // Ensure originalGetStyle exists before calling it
            const style =
              originalGetStyle && typeof originalGetStyle === 'function'
                ? originalGetStyle.call(this, prop)
                : undefined;

            const view = this.getView?.();
            const el = view?.el;

            if (!isHTMLElement(el) || typeof window === 'undefined') {
              return style;
            }

            // Check for inline style
            if (prop && hasInlineStyle(el, prop)) {
              return style;
            }

            // If getting a specific property, return computed value
            if (prop) {
              const computedValue = getComputedStyleValue(el, prop);
              if (computedValue && isValidCssValue(computedValue)) {
                let normalized = prop === 'color' ? normalizeColor(computedValue) : computedValue;
                normalized = sanitizeCssValue(normalized);
                if (normalized && isValidCssValue(normalized)) {
                  return normalized;
                }
              }
              return style;
            }

            // If getting all styles, merge computed values for properties without inline styles
            // Also sanitize original style values to prevent invalid tokens
            if (!prop && style && typeof style === 'object') {
              const mergedStyle: any = {};
              const styleProps = Object.keys(style);

              // First, sanitize and copy original style values
              for (const styleProp of styleProps) {
                if (isValidCssPropertyName(styleProp)) {
                  const originalValue = style[styleProp];
                  if (originalValue != null && isValidCssValue(String(originalValue))) {
                    const sanitized = sanitizeCssValue(String(originalValue));
                    if (sanitized && isValidCssValue(sanitized)) {
                      mergedStyle[styleProp] = sanitized;
                    }
                  }
                }
              }

              // Then, update with computed values for properties that don't have inline styles
              for (const styleProp of styleProps) {
                if (!hasInlineStyle(el, styleProp)) {
                  const computedValue = getComputedStyleValue(el, styleProp);
                  if (computedValue && isValidCssValue(computedValue)) {
                    let normalized = styleProp === 'color' ? normalizeColor(computedValue) : computedValue;
                    normalized = sanitizeCssValue(normalized);
                    if (normalized && isValidCssValue(normalized)) {
                      mergedStyle[styleProp] = normalized;
                    }
                  }
                }
              }

              return mergedStyle;
            }

            return style;
          };
        }
      }

      // Also listen for component selection to sync styles
      // This explicitly synchronizes rule styles when a component is selected,
      // separating mutation concerns from read operations.
      // Deferred to avoid conflicts with frame rendering.
      const onComponentSelected = (component: any) => {
        if (!component) return;

        const view = component.getView?.();
        const el = view?.el;
        if (!isHTMLElement(el) || typeof window === 'undefined') return;

        // Validate that CssComposer is still available
        if (!CssComposer || typeof CssComposer !== 'object') return;

        // Helper function to safely get rules for a component
        // Instead of calling getRules (which can fail with invalid component state),
        // we get all rules and filter them manually based on component selectors
        // This avoids the internal GrapesJS bug where getRules calls .indexOf() on invalid data
        const safeGetRules = (comp: any): any[] => {
          if (!comp || !CssComposer) return [];

          try {
            // Validate component has expected structure
            if (typeof comp !== 'object') return [];

            // Get component's classes and id for matching
            let componentClasses: string[] = [];
            let componentId = '';

            try {
              const classes = comp.getClasses?.() || [];
              componentClasses = Array.isArray(classes) ? classes.filter((c: any) => typeof c === 'string') : [];
              componentId = comp.getId?.() || comp.get?.('id') || '';
            } catch (error) {
              // If we can't get component properties, return empty array
              return [];
            }

            // Get all rules and filter manually to avoid the GrapesJS bug
            const allRulesResult = CssComposer.getAll?.();
            if (!allRulesResult) return [];

            const allRules =
              allRulesResult && typeof allRulesResult.toArray === 'function'
                ? allRulesResult.toArray()
                : Array.isArray(allRulesResult)
                  ? allRulesResult
                  : [];

            // Filter rules that match this component
            const matchingRules: any[] = [];

            for (const rule of allRules) {
              try {
                const selectors = rule.getSelectors?.() || [];
                const validSelectors = Array.isArray(selectors)
                  ? selectors.filter((s: any) => typeof s === 'string')
                  : [];

                // Check if any selector matches the component
                const matches = validSelectors.some((sel: string) => {
                  // Match by ID
                  if (componentId && sel.includes(`#${componentId}`)) {
                    return true;
                  }
                  // Match by class
                  return componentClasses.some((cls: string) => sel.includes(`.${cls}`) || sel === cls);
                });

                if (matches) {
                  matchingRules.push(rule);
                }
              } catch (error) {
                // Skip rules that cause errors
                continue;
              }
            }

            return matchingRules;
          } catch (error) {
            // If anything fails, return empty array instead of throwing
            console.warn('Error getting rules for component:', error);
            return [];
          }
        };

        // Defer style synchronization to avoid conflicts with frame rendering
        // Use multiple requestAnimationFrame calls to ensure rendering is complete
        if (typeof window !== 'undefined' && window.requestAnimationFrame) {
          // Use double requestAnimationFrame to ensure rendering is complete
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              // Check if we're already synchronizing to prevent concurrent updates
              if (isSynchronizingRef.current) {
                return;
              }
              isSynchronizingRef.current = true;

              try {
                // Get rules for the component using safeGetRules
                // We use safeGetRules directly instead of originalGetRulesFunc to avoid
                // the known GrapesJS bug where getRules calls .indexOf() on invalid data
                const rules = safeGetRules(component);

                // Synchronize rule styles with computed styles
                if (rules && rules.length > 0) {
                  synchronizeRuleStyles(component, rules, el);
                }
              } catch (error) {
                // Silently handle errors to avoid breaking component selection
                console.warn('Error synchronizing styles on component selection:', error);
              } finally {
                // Reset synchronization flag after a short delay
                if (typeof window !== 'undefined' && window.setTimeout) {
                  window.setTimeout(() => {
                    isSynchronizingRef.current = false;
                  }, 100);
                } else {
                  isSynchronizingRef.current = false;
                }
              }
            });
          });
        } else if (typeof window !== 'undefined' && window.setTimeout) {
          // Fallback to setTimeout if requestAnimationFrame is not available
          // Use a longer delay to ensure rendering is complete
          window.setTimeout(() => {
            // Check if we're already synchronizing to prevent concurrent updates
            if (isSynchronizingRef.current) {
              return;
            }
            isSynchronizingRef.current = true;

            try {
              // Get rules for the component using safeGetRules
              // We use safeGetRules directly instead of originalGetRulesFunc to avoid
              // the known GrapesJS bug where getRules calls .indexOf() on invalid data
              const rules = safeGetRules(component);

              // Synchronize rule styles with computed styles
              if (rules && rules.length > 0) {
                synchronizeRuleStyles(component, rules, el);
              }
            } catch (error) {
              // Silently handle errors to avoid breaking component selection
              console.warn('Error synchronizing styles on component selection:', error);
            } finally {
              // Reset synchronization flag after a short delay
              if (typeof window !== 'undefined' && window.setTimeout) {
                window.setTimeout(() => {
                  isSynchronizingRef.current = false;
                }, 100);
              } else {
                isSynchronizingRef.current = false;
              }
            }
          }, 50);
        }
      };

      // Store handler reference for cleanup
      componentSelectedHandlerRef.current = onComponentSelected;
      editor.on('component:selected', onComponentSelected);
    };

    // Initialize when editor is ready
    if (editor.CssComposer) {
      initFix();
    } else {
      editor.once('load', initFix);
    }

    return () => {
      // Clear any pending timeout when component unmounts
      if (timeoutIdRef.current !== null && typeof window !== 'undefined' && window.clearTimeout) {
        window.clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }

      // Remove component:selected listener if it was registered
      if (editor && editor.off && componentSelectedHandlerRef.current) {
        editor.off('component:selected', componentSelectedHandlerRef.current);
        componentSelectedHandlerRef.current = null;
      }

      // Restore original functions
      const originals = originalFunctionsRef.current;

      // Restore CssRule.prototype.getStyle
      if (originals.cssRuleGetStyle && originals.cssRuleGetStyle.prototype) {
        originals.cssRuleGetStyle.prototype.getStyle = originals.cssRuleGetStyle.original;
        originals.cssRuleGetStyle = null;
      }

      // Restore CssComposer.getRules
      if (originals.cssComposerGetRules && originals.cssComposerGetRules.instance) {
        originals.cssComposerGetRules.instance.getRules = originals.cssComposerGetRules.original;
        originals.cssComposerGetRules = null;
      }

      // Restore Component.prototype.getStyle
      if (originals.componentGetStyle && originals.componentGetStyle.prototype) {
        originals.componentGetStyle.prototype.getStyle = originals.componentGetStyle.original;
        originals.componentGetStyle = null;
      }
    };
  }, [editor]);
};

export default useStyleSpecificityFix;
